// https://github.com/angular/angularfire/blob/main/docs/firestore.md

import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Entry } from 'src/services/entry/Entry';
import { EntryService } from 'src/services/entry/entry.service';
import {
  Firestore,
  collectionData,
  collection,
  query,
  addDoc,
  Timestamp,
  deleteDoc,
  doc,
  updateDoc,
} from '@angular/fire/firestore';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class FirestoreEntryServiceService {
  firestore: Firestore = inject(Firestore);
  authService: AuthService = inject(AuthService);

  public entries = new BehaviorSubject<Entry[]>([]);

  private getCollectionPath() {
    return `/users/${this.authService.user?.uid}/entries`;
  }

  constructor() {
    const itemCollection = collection(this.firestore, this.getCollectionPath());

    const entriesObservable = collectionData(query(itemCollection), {
      idField: 'id',
    }) as Observable<Entry[]>;

    entriesObservable.subscribe((entries) => {
      const mappedEntries = entries.map((e) => {
        return {
          id: e.id,
          memo: e.memo ?? '',
          tagIds: e.tagIds ?? [],
          amount: e.amount ?? 0,
          date: e.date
            ? new Timestamp(
                (e.date as any).seconds,
                (e.date as any).nanoseconds
              ).toDate()
            : new Date(),
        };
      });

      this.entries.next(mappedEntries);
    });
  }

  async put(entry: Entry): Promise<void> {
    // TODO: DUPLICATE
    // TODO: creating entry with tag fails for some reason
    const cleanedEntry = Object.fromEntries(
      Object.entries(entry).filter(([_, value]) => value !== undefined)
    );

    cleanedEntry['tagIds'] = cleanedEntry['tagIds'].filter((tagId: string) => {
      return tagId !== undefined;
    });

    await updateDoc(
      doc(this.firestore, this.getCollectionPath() + '/' + entry.id),
      { ...cleanedEntry }
    );
  }

  async post(entry: Entry): Promise<void> {
    const cleanedEntry = Object.fromEntries(
      Object.entries(entry).filter(
        ([key, value]) => value !== undefined && key !== 'tagFullPaths'
      )
    );

    cleanedEntry['tagIds'] = cleanedEntry['tagIds'].filter((tagId: string) => {
      return tagId !== undefined;
    });

    await addDoc(
      collection(
        this.firestore,
        `/users/${this.authService.user?.uid}/entries`
      ),
      cleanedEntry
    );
  }

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(this.firestore, this.getCollectionPath() + '/' + id));
  }
}
